// components/AdminDashboard/AdminAdsSection.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

/**
 * AdminAdsSection - A reusable card component for admin dashboard ads/actions
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.icon - Icon element to display
 * @param {string} props.title - Main title text
 * @param {string} props.subtitle - Subtitle description
 * @param {string} props.className - Additional custom classes
 * @param {function} props.onClick - Click handler function
 * @param {string} props.variant - Variant style ('default', 'primary', 'outline')
 * @param {string} props.link - Link URL for navigation
 */
function AdminAdsSection({
  icon = null,
  title = "",
  subtitle = "",
  className = "",
  onClick = null, // Fixed: Changed from "" to null
  variant = "default",
  link = "",
  ...restProps
}) {
  // Define variant styles
  const variantStyles = {
    default: "bg-white border-[#E5E5E5] text-[#101828]",
    primary: "bg-[#0D924F] border-[#0D924F] text-white",
    outline: "bg-transparent border-2 border-[#0D924F] text-[#0D924F]"
  };

  // Map icon or use default plus icon
  const renderIcon = () => {
    if (icon) {
      return icon;
    }
    
    // Default plus icon (SVG)
    return (
      <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 m-5">
        <svg 
          width="40" 
          height="40" 
          viewBox="0 0 40 40" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="20" cy="20" r="15" fill={variant === 'primary' ? 'white' : '#0D924F'} />
          <rect 
            x="12.5" 
            y="19" 
            width="15" 
            height="2" 
            fill={variant === 'primary' ? '#0D924F' : 'white'} 
          />
          <rect 
            x="19" 
            y="12.5" 
            width="2" 
            height="15" 
            fill={variant === 'primary' ? '#0D924F' : 'white'} 
          />
        </svg>
      </div>
    );
  };

  const handleClick = (e) => {
    if (onClick) {
      onClick(e);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (onClick) {
        onClick(e);
      }
    }
  };

  const cardContent = (
    <div
      className={`
        w-[354px] h-[160px] 
        border rounded-[12px] 
        mt-20 ml-20
        p-10 
        flex items-center gap-8 
        transition-all duration-200 hover:shadow-lg cursor-pointer
        ${variantStyles[variant]}
        ${className}
      `}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      {...restProps}
    >
      {/* Icon Section */}
      <div className="flex-shrink-0">
        {renderIcon()}
      </div>

      {/* Text Content Section */}
      <div className="flex flex-col gap-3 flex-1">
        <h3 className="font-medium text-[24px] leading-9 text-current">
          {title}
        </h3>
        <p className="font-normal text-[14px] leading-[21px] text-[#898989]">
          {subtitle}
        </p>
      </div>
    </div>
  );

  // If link is provided, wrap in Link component
  if (link) {
    return (
      <Link to={link}>
        {cardContent}
      </Link>
    );
  }

  return cardContent;
}

// PropTypes for type checking
AdminAdsSection.propTypes = {
  icon: PropTypes.node,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  className: PropTypes.string,
  onClick: PropTypes.func,
  variant: PropTypes.oneOf(['default', 'primary', 'outline']),
  link: PropTypes.string
};

// Default props
AdminAdsSection.defaultProps = {
  icon: null,
  title: "",
  subtitle: "",
  className: "",
  onClick: null, // Fixed: Changed from "" to null
  variant: "default",
  link: ""
};

export default AdminAdsSection;